import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in child component tree
 * Displays a fallback UI instead of crashing the whole app
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  onRetry?: () => void;
}

/**
 * Default fallback UI for error boundaries
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  onRetry,
}) => {
  return (
    <div className="d-flex flex-column flex-center min-h-300px p-10">
      <div className="card shadow-sm border-0 max-w-500px w-100">
        <div className="card-body text-center">
          {/* Error Icon */}
          <div className="mb-5">
            <i className="bi bi-exclamation-triangle text-danger fs-3x"></i>
          </div>

          {/* Error Title */}
          <h3 className="text-gray-800 fw-bold mb-3">Something went wrong</h3>

          {/* Error Message */}
          <p className="text-gray-600 mb-5">
            We encountered an unexpected error. Please try again or contact
            support if the problem persists.
          </p>

          {/* Error Details (Development Only) */}
          {process.env.NODE_ENV === "development" && error && (
            <div className="bg-light-danger rounded p-4 mb-5 text-start">
              <code className="text-danger fs-7 d-block overflow-auto">
                {error.message}
              </code>
              {error.stack && (
                <pre className="text-muted fs-8 mt-2 mb-0 overflow-auto" style={{ maxHeight: "200px" }}>
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-center gap-3">
            {onRetry && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onRetry}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Try Again
              </button>
            )}
            <button
              type="button"
              className="btn btn-light"
              onClick={() => window.location.reload()}
            >
              <i className="bi bi-arrow-repeat me-2"></i>
              Reload Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact error fallback for dashboard widgets.
 * Sized to replace the widget in-place without disrupting page layout.
 */
export const WidgetErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <div
    style={{
      background: "#FFF1F2",
      border: "1px solid #FEE2E2",
      borderRadius: 8,
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 24,
    }}
  >
    <i className="bi bi-exclamation-triangle" style={{ fontSize: 18, color: "#EF4444", flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
        Unable to load this widget
      </div>
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
        An unexpected error occurred. This doesn't affect the rest of the page.
      </div>
    </div>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        style={{
          background: "transparent",
          border: "1px solid #FCA5A5",
          borderRadius: 6,
          padding: "4px 10px",
          fontSize: 12,
          fontWeight: 500,
          color: "#DC2626",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Retry
      </button>
    )}
  </div>
);

/**
 * Higher-order component to wrap a component with error boundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
